import { alertWithMessage } from "components/modal/message_modal";
import global from "lib/config/global";
import { AppReducerStateProps, store } from "lib/store";
import { ufabetGameModalActionTypes } from "lib/store/ufabetGameModal/reducer";
import getinfo from "lib/worker/api/getinfo";
import startGameWithAutoRegister from "lib/worker/api/start_game_with_auto_register";
import ufabetGetBalance from "lib/worker/api/ufabet_get_balance";
import ufabetTransferIn from "lib/worker/api/ufabet_transfer_in";
import ufabetTransferOut from "lib/worker/api/ufabet_transfer_out";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import Img from "react-cloudimage-responsive";
import { connect } from "react-redux";

export interface UfabetStartGameModalProps {
  closeModal: Function;
  state: AppReducerStateProps;
}

/**
 * Function สำหรับเปิด modal เริ่มเกม UFABET
 * เรียกใช้ function นี้เพื่อแสดง modal
 *
 * @example
 * import { openUfabetStartGameModal } from "lib/store/ufabetGameModal/actions";
 *
 * // ใช้งาน
 * openUfabetStartGameModal();
 */
export const openUfabetStartGameModal = () => {
  store.dispatch({
    type: ufabetGameModalActionTypes.OPEN_UFABET_GAME_MODAL,
  });
};

/**
 * Function สำหรับปิด modal เริ่มเกม UFABET
 * เรียกใช้ function นี้เพื่อปิด modal
 */
export const closeUfabetStartGameModal = () => {
  store.dispatch({
    type: ufabetGameModalActionTypes.CLOSE_UFABET_GAME_MODAL,
  });
};

function UfabetStartGameModal(props: UfabetStartGameModalProps) {
  const { closeModal } = props;
  const { isOpen } = props.state.ufabetGameModal;
  const router = useRouter();
  const { prefix } = global().gobalVariable;
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const memberBalance = props.state.member.getinfo.Balance ?? 0;
  const [ufabetBalance, setUfabetBalance] = useState<number>(0);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);

  const fetchBalances = async () => {
    setIsLoadingBalance(true);
    try {
      await getinfo();
      const ufabetBalanceResponse = await ufabetGetBalance();
      // ตั้งค่า UFABET balance
      if (ufabetBalanceResponse?.status === "success") {
        setUfabetBalance(ufabetBalanceResponse.data?.balance ?? 0);
      }
    } catch (error) {
    } finally {
      setIsLoadingBalance(false);
    }
  };

  const isLogin = props.state.member?.isLogin ?? false;

  useEffect(() => {
    if (isOpen) {
      // If user is not logged in, skip fetching balances and show login prompt
      if (!isLogin) return;
      (async () => {
        await fetchBalances();
      })();
    }
  }, [isOpen, isLogin]);

  // Handle modal open/close animations
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  const handleClose = () => {
    if (!isLoading) {
      setIsClosing(true);
      setTimeout(() => {
        closeModal();
      }, 300); // Match animation duration
    }
  };

  const handleStartGame = async () => {
    // Do not call APIs when user is not logged in
    if (!isLogin) {
      alertWithMessage("กรุณาเข้าสู่ระบบก่อน");
      handleClose();
      router.push(`/login/${prefix}`);
      return;
    }
    setIsLoading(true);
    try {
      const device = /Mobile|Android|iPhone|iPad|iPod/i.test(
        navigator.userAgent
      )
        ? "mobile"
        : "desktop";

      await startGameWithAutoRegister({
        Device: device,
      });

      // ปิด modal หลังจากเปิดเกมสำเร็จ
      handleClose();
    } catch (error) {
      console.error("Error starting UFABET game:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransferIn = async () => {
    // Do not call APIs when user is not logged in
    if (!isLogin) {
      alertWithMessage("กรุณาเข้าสู่ระบบก่อน");
      handleClose();
      router.push(`/login/${prefix}`);
      return;
    }
    if (!memberBalance || memberBalance <= 0) {
      alertWithMessage("ยอดเงินระบบไม่เพียงพอ");
      return;
    }

    setIsTransferring(true);
    try {
      const response = await ufabetTransferIn({ amount: memberBalance });
      if (response?.status === "success") {
        await fetchBalances();
      } else {
        alertWithMessage(response?.message || "เกิดข้อผิดพลาดในการโยกเงิน");
      }
    } catch (error) {
      console.error("Error transferring in:", error);
      alertWithMessage("เกิดข้อผิดพลาดในการโยกเงิน");
    } finally {
      setIsTransferring(false);
    }
  };

  const handleTransferOut = async () => {
    // Do not call APIs when user is not logged in
    if (!isLogin) {
      alertWithMessage("กรุณาเข้าสู่ระบบก่อน");
      handleClose();
      router.push(`/login/${prefix}`);
      return;
    }
    if (!ufabetBalance || ufabetBalance <= 0) {
      alertWithMessage("ยอดเงิน UFABET ไม่เพียงพอ");
      return;
    }

    setIsTransferring(true);
    try {
      const response = await ufabetTransferOut({ amount: ufabetBalance });
      if (response?.status === "success") {
        await fetchBalances();
      } else {
        alertWithMessage(response?.message || "เกิดข้อผิดพลาดในการโยกเงิน");
      }
    } catch (error) {
      console.error("Error transferring out:", error);
      alertWithMessage("เกิดข้อผิดพลาดในการโยกเงิน");
    } finally {
      setIsTransferring(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className={`fixed inset-0 z-[9999] bg-black/50 backdrop-blur-md flex items-center justify-center p-0 overflow-hidden transition-all duration-300 ${
          isClosing ? "opacity-0" : "opacity-100"
        }`}
        onClick={(e) => {
          if (e.target === e.currentTarget && !isLoading) {
            handleClose();
          }
        }}
        style={{
          zIndex: 9999,
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
        }}
      >
        {/* Scrollable Container */}
        <div
          className="w-full h-full overflow-y-auto flex items-center justify-center p-4 sm:p-6 pt-14 pb-16 sm:pt-10 sm:pb-12"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isLoading) {
              handleClose();
            }
          }}
        >
          {/* Modal Container */}
          <div
            className={`relative w-full max-w-md rounded-3xl shadow-2xl transform transition-all duration-300 ease-out flex flex-col h-auto max-h-[75vh] sm:max-h-[70vh] mt-4 sm:mt-0 ${
              isClosing
                ? "opacity-0 scale-90 translate-y-8"
                : "opacity-100 scale-100 translate-y-0"
            }`}
            style={{
              background: "rgba(0, 0, 0, 0.85)",
              border: "1px solid #C9A961",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5), 0 0 1px #E8D4A0 inset",
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {/* Close Button */}
            {!isLoading && (
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 z-10 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 hover:scale-110 active:scale-95 shadow-xl border-2 overflow-hidden group"
                style={{
                  background:
                    "linear-gradient(135deg, #0a0a0a 0%, #1a0f0a 30%, #000000 70%, #0a0a0a 100%)",
                  borderColor: "#C9A961",
                  color: "#F4D94C",
                  boxShadow:
                    "0 0 0 1px rgba(201, 169, 97, 0.3), 0 4px 12px rgba(0, 0, 0, 0.8), 0 0 20px rgba(201, 169, 97, 0.2), inset 0 1px 0 rgba(245, 217, 76, 0.1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, #1a0f0a 0%, #2a1f0a 30%, #0a0a0a 70%, #1a0f0a 100%)";
                  e.currentTarget.style.boxShadow =
                    "0 0 0 1px rgba(201, 169, 97, 0.5), 0 6px 20px rgba(0, 0, 0, 0.9), 0 0 35px rgba(244, 217, 76, 0.4), inset 0 1px 0 rgba(245, 217, 76, 0.2)";
                  e.currentTarget.style.transform = "scale(1.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    "linear-gradient(135deg, #0a0a0a 0%, #1a0f0a 30%, #000000 70%, #0a0a0a 100%)";
                  e.currentTarget.style.boxShadow =
                    "0 0 0 1px rgba(201, 169, 97, 0.3), 0 4px 12px rgba(0, 0, 0, 0.8), 0 0 20px rgba(201, 169, 97, 0.2), inset 0 1px 0 rgba(245, 217, 76, 0.1)";
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title={"ปิด"}
              >
                {/* Shine effect */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background:
                      "linear-gradient(135deg, transparent 0%, rgba(245, 217, 76, 0.15) 50%, transparent 100%)",
                  }}
                />
                <span className="text-xl font-bold leading-none relative z-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                  ×
                </span>
              </button>
            )}

            {/* Modal Header */}
            <div className="px-6 pt-8 pb-4 flex-shrink-0">
              {/* Logo & Title */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative w-[120px] border border-[#C9A961] rounded-lg p-2 bg-black mb-3 shadow-[0_0_15px_rgba(201,169,97,0.3)]">
                  <Img src="logo-ufabet.png"></Img>
                </div>
                <div className="text-center">
                  <h2 className="text-xl font-bold text-[#F4D94C] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                    UFABET CLASSIC
                  </h2>
                  <p className="text-gray-400 text-sm">
                    ระบบโยกเงิน (Credit Transfer)
                  </p>
                </div>
              </div>

              {/* Warning Message */}
              {/* If user is not logged in, show login prompt */}
              {!isLogin && (
                <div className="flex flex-col items-center mb-3">
                  <span className="px-3 py-1 text-sm font-semibold text-red-400 mb-2">
                    {"กรุณาเข้าสู่ระบบเพื่อใช้งานฟีเจอร์นี้"}
                  </span>
                  <button
                    onClick={() => {
                      // close modal then navigate to login page
                      handleClose();
                      router.push(`/login/${prefix}`);
                    }}
                    className="px-4 py-2 rounded-lg font-semibold bg-yellow-400 text-black"
                  >
                    {"ไปยังหน้าล็อกอิน"}
                  </button>
                </div>
              )}

              {isLogin && memberBalance === 0 && (
                <div className="flex justify-center mb-3">
                  <span className="px-3 py-1 text-xs font-semibold text-blue-400">
                    *กรุณาเติมเงินเข้าระบบก่อนเข้าเล่น
                  </span>
                </div>
              )}

              {/* Balance Information */}
              <div className="space-y-3">
                {/* Member Balance */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gradient-to-r from-amber-900 to-amber-950 rounded-lg px-2 py-2 border border-amber-900 flex items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-300 whitespace-nowrap">
                        ระบบ:
                      </span>
                      <span className="text-base font-bold text-blue-400">
                        {isLoadingBalance ? (
                          <span className="text-gray-400">
                            {"กำลังโหลด..."}
                          </span>
                        ) : memberBalance !== null ? (
                          `${memberBalance.toFixed(2)}`
                        ) : (
                          "0.00"
                        )}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleTransferIn}
                    disabled={
                      isTransferring ||
                      !memberBalance ||
                      memberBalance <= 0 ||
                      !isLogin
                    }
                    className="w-24 px-2 py-2 text-xs rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap shadow-md"
                    style={{
                      background:
                        !memberBalance || memberBalance <= 0
                          ? "#666666"
                          : "rgb(251, 191, 36)",
                      color: "#FFFFFF",
                      textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    {isTransferring ? (
                      <span className="flex items-center justify-center gap-1">
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </span>
                    ) : (
                      "โยกเข้า UFA"
                    )}
                  </button>
                </div>

                {/* UFABET Balance */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gradient-to-r from-amber-900 to-amber-950 rounded-lg px-2 py-2 border border-amber-900 flex items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-300 whitespace-nowrap">
                        UFABET:
                      </span>
                      <span className="text-base font-bold text-yellow-400">
                        {isLoadingBalance ? (
                          <span className="text-gray-400">
                            {"กำลังโหลด..."}
                          </span>
                        ) : ufabetBalance !== null ? (
                          `${ufabetBalance.toFixed(2)}`
                        ) : (
                          "0.00"
                        )}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleTransferOut}
                    disabled={
                      isTransferring ||
                      !ufabetBalance ||
                      ufabetBalance <= 0 ||
                      !isLogin
                    }
                    className="w-24 px-2 py-2 text-xs rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    style={{
                      background:
                        !ufabetBalance || ufabetBalance <= 0
                          ? "#666666"
                          : "rgb(59, 130, 246)",
                      color: "#FFFFFF",
                      textShadow: "0 2px 4px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    {isTransferring ? (
                      <span className="flex items-center justify-center gap-1">
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      </span>
                    ) : (
                      "โยกเข้า ระบบ"
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className={`px-6 pb-6 rounded-b-3xl flex-shrink-0 pt-4 transition-all duration-300 delay-150 border-t border-gray-800 ${
                isClosing
                  ? "opacity-0 translate-y-4"
                  : "opacity-100 translate-y-0"
              }`}
              style={{ background: "rgba(0, 0, 0, 0.85)" }}
            >
              <div className="flex gap-4">
                <button
                  onClick={handleStartGame}
                  disabled={!isLogin}
                  className="w-full px-6 py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-base transform hover:scale-105 active:scale-95"
                  style={{
                    background:
                      "linear-gradient(135deg, #1a1a1a 0%, #000000 50%, #0a0a0a 100%)",
                    color: "#C9A961",
                    border:
                      !ufabetBalance || ufabetBalance <= 0
                        ? "1px solid #333333"
                        : "none",
                    borderRadius: "20px",
                    boxShadow:
                      !ufabetBalance || ufabetBalance <= 0
                        ? "0 0 10px rgba(100, 100, 100, 0.2), inset 0 1px 0 rgba(80, 80, 80, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.3)"
                        : "0 0 20px rgba(201, 169, 97, 0.4), inset 0 1px 0 rgba(201, 169, 97, 0.2), inset 0 -1px 0 rgba(0, 0, 0, 0.5)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    if (!ufabetBalance || ufabetBalance <= 0) return;
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 50%, #0a0a0a 100%)";
                    e.currentTarget.style.border = "none";
                    e.currentTarget.style.boxShadow =
                      "0 0 25px rgba(201, 169, 97, 0.6), inset 0 1px 0 rgba(201, 169, 97, 0.3), inset 0 -1px 0 rgba(0, 0, 0, 0.7)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #1a1a1a 0%, #000000 50%, #0a0a0a 100%)";
                    e.currentTarget.style.border =
                      !ufabetBalance || ufabetBalance <= 0
                        ? "1px solid #333333"
                        : "none";
                    e.currentTarget.style.boxShadow =
                      !ufabetBalance || ufabetBalance <= 0
                        ? "0 0 10px rgba(100, 100, 100, 0.2), inset 0 1px 0 rgba(80, 80, 80, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.3)"
                        : "0 0 20px rgba(201, 169, 97, 0.4), inset 0 1px 0 rgba(201, 169, 97, 0.2), inset 0 -1px 0 rgba(0, 0, 0, 0.5)";
                  }}
                >
                  {/* Glossy overlay effect */}
                  {ufabetBalance > 0 && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "50%",
                        background:
                          "linear-gradient(135deg, rgba(201, 169, 97, 0.1) 0%, rgba(201, 169, 97, 0.05) 50%, transparent 100%)",
                        pointerEvents: "none",
                        borderRadius: "17px 17px 0 0",
                      }}
                    />
                  )}
                  {isLoading ? (
                    <span
                      className="flex items-center justify-center gap-2"
                      style={{ color: "#C9A961" }}
                    >
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        style={{ color: "#C9A961" }}
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {"กำลังเปิดเกม..."}
                    </span>
                  ) : (
                    <span
                      className="flex items-center justify-center gap-2"
                      style={{
                        color: "#C9A961",
                      }}
                    >
                      เข้าเล่น
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state: AppReducerStateProps) => {
  return {
    state: state,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    closeModal: () => {
      dispatch({
        type: ufabetGameModalActionTypes.CLOSE_UFABET_GAME_MODAL,
      });
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UfabetStartGameModal);
